import styled from '@emotion/styled';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import Tooltip from '@atlaskit/tooltip';
import { DTOAllCustomfieldsAndUserCapabilities, DTOCustomField, ErrorOrPayload, GET_ALL_CUSTOM_FIELDS, USER_CAPABILITY_GRANT_PERMISSIONS } from '../generated-common';
import { GlobalContext } from '../shared-components/common/GlobalContext';
import { BetterDynamicTable, BetterTableHead, BetterTablePaginationState, BetterTableRow, calculatePaginationState } from '../shared-components/common/BetterDynamicTable';
import { LoadingState } from '../model/model';
import { PullRight } from '../shared-components/stateless/PullRight';
import Button from '@atlaskit/button';
import { CutIfTooBig } from '../shared-components/stateless/CutIfTooBig';
import { NoWrap } from '../shared-components/stateless/NoWrap';
import { CepCustomFieldTypeIcon } from '../shared-components/stateless/CepCustomFieldTypeIcon';
import { CepPageHeader, TBreadcrumbsItem } from '../shared-components/stateless/CepPageHeader';
import { PageWrapper } from '../shared-components/stateless/layout/PageWrapper';
import { prettifyCustomFieldId } from '../shared-components/util/id-util';
import { PermissionEditorEditPermissions } from '../components/permission-editor/PermissionEditorEditPermissions';
import { AtlassianLockedWithoutBorder, AtlassianWarnWithoutBorder, ChessPawnIcon, ChessQueenIcon } from '../shared-components/common/CustomIcons';
import Icon from '@atlaskit/icon';
import { PageLoadError } from 'components/ui/PageLoadError';
import { token } from '@atlaskit/tokens';

const InnerTd = styled.div<{ isFirst: boolean; isLast: boolean; isHiddenOnNonFirst: boolean }>`
  padding-right: 8px;
  padding-left: 8px;
  padding-top: ${(props: any) => (props.isFirst ? '10px' : '10px')};
  padding-bottom: ${(props: any) => (props.isLast ? '6px' : '6px')};
  display: ${(props: any) => (!props.isFirst && props.isHiddenOnNonFirst ? 'none' : 'block')};
`;

const InnerTh = styled.div`
  padding-left: 8px;
`;

export const FieldEditorListFieldsPage = () => {
  const [customFields, setCustomFields] = useState<DTOCustomField[]>([]);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [errorId, setErrorId] = useState<null | string>(null);

  const [userCapabilities, setUserCapabilities] = useState<string[]>([]);

  const [currentFilter, setCurrentFilter] = useState<string>('');
  const [currentPaginationState, setCurrentPaginationState] = useState<BetterTablePaginationState>({
    currentPage: 1,
    totalPages: 1,
    processingTimeInMs: 0,
  });
  const [customFieldsLoadingState, setCustomFieldsLoadingState] = useState(LoadingState.NOT_LOADED);

  const { t, tWithIcon, invoke, encodeUriSafe } = useContext(GlobalContext);
  const navigate = useNavigate();

  const breadcrumbItems: TBreadcrumbsItem[] = [
    {
      href: '/fe/fields',
      i18n: 'user.list.breadcrumbTitle',
      params: [],
    },
  ];
  useEffect(() => {
    setCustomFieldsLoadingState(LoadingState.LOADING);
    const maxResults = 10;
    const startAt = (currentPaginationState.currentPage - 1) * maxResults;
    invoke(GET_ALL_CUSTOM_FIELDS, { startAt: startAt, maxResults: maxResults, currentFilter: currentFilter }).then((result: any) => {
      const typedResult: ErrorOrPayload<DTOAllCustomfieldsAndUserCapabilities> = result;
      if (typedResult.hasError) {
        setCustomFields([]);
        setCustomFieldsLoadingState(LoadingState.FAILED);
        setHasError(true);
        setErrorId(typedResult.errorId);
        setError(typedResult.errorMessage ? typedResult.errorMessage : 'Unexpected error');
      } else {
        setHasError(false);
        setError(null);
        setErrorId(null);
        setCustomFields(typedResult.payload.fields.values);
        setUserCapabilities(typedResult.payload.userCapabilities);
        setCurrentPaginationState(
          calculatePaginationState(typedResult.payload.fields.startAt, typedResult.payload.fields.maxResults, typedResult.payload.fields.total, typedResult.payload.fields.processingTimeInMs)
        );
        setCustomFieldsLoadingState(LoadingState.LOADED);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter, currentPaginationState.currentPage]);

  const head: BetterTableHead = {
    cells: [
      {
        key: 'fieldType',
        content: <InnerTh>{t('user.list.tableHead.type')}</InnerTh>,
        isSortable: false,
        widthPercent: '6%',
      },
      {
        key: 'fieldId',
        content: <InnerTh>{t('user.list.tableHead.id')}</InnerTh>,
        isSortable: false,
        widthPercent: '6%',
      },
      {
        key: 'fieldName',
        content: <InnerTh>{t('user.list.tableHead.name')}</InnerTh>,
        isSortable: false,
        widthPercent: '24%',
      },
      {
        key: 'fieldPermission',
        content: <InnerTh>{t('user.list.tableHead.permission')}</InnerTh>,
        isSortable: false,
        widthPercent: '4%',
      },
      {
        key: 'actions',
        content: '',
        isSortable: false,
        widthPercent: '8%',
      },
    ],
  };

  const rows: BetterTableRow[] = [];
  if (customFields) {
    for (let index = 0; index < customFields.length; index++) {
      const customField: DTOCustomField = customFields[index];

      rows.push({
        key: `row-${customField.fieldId}-${index}-` + Math.random(),
        doHoverTr: true,
        cells: [
          {
            key: `type-${index}-${customField.fieldId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                <CepCustomFieldTypeIcon type={customField.fieldType} size="forlist" />
              </InnerTd>
            ),
          },
          {
            key: `id-${index}-${customField.fieldId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                <NoWrap>{prettifyCustomFieldId(customField.fieldId)}</NoWrap>
              </InnerTd>
            ),
          },
          {
            key: `name-${index}-${customField.fieldId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                <span style={{ color: token('color.text.accent.blue.bolder', '#253858'), fontWeight: 500, wordBreak: 'break-all' }}>{customField.fieldName}</span>
                <br />
                <CutIfTooBig tooltipText={customField.fieldDescription} color={token('color.text.accent.gray', '#7A869A')} text={customField.fieldDescription} />
              </InnerTd>
            ),
          },
          {
            key: `permtype-${index}-${customField.fieldId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                {customField.fieldIsCompatible === false ? (
                  <Tooltip content={t('user.list.fieldIsNotCompatible')}>
                    <Icon glyph={AtlassianWarnWithoutBorder} label="incompatible" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="small" />
                  </Tooltip>
                ) : customField.fieldIsLocked ? (
                  <Tooltip content={t('user.list.fieldIsLocked')}>
                    <Icon glyph={AtlassianLockedWithoutBorder} label="locked" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="small" />
                  </Tooltip>
                ) : customField.fieldGlobalPermission ? (
                  <Tooltip content={t('user.list.hasGlobalPermission.yes')}>
                    <Icon glyph={ChessQueenIcon} label="chessqueen" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="small" />
                  </Tooltip>
                ) : (
                  <Tooltip content={t('user.list.hasGlobalPermission.no')}>
                    <Icon glyph={ChessPawnIcon} label="chesspawn" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="small" />
                  </Tooltip>
                )}
              </InnerTd>
            ),
          },
          {
            key: `actions-${index}-${customField.fieldId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                {customField.fieldId ? (
                  <div style={{ display: 'block', marginTop: '-4px' }} data-e2e-edit-field-actions={`${customField.fieldId}`}>
                    {customField.fieldIsLocked !== true && customField.fieldIsCompatible === true && (
                      <PullRight>
                        {userCapabilities.includes(USER_CAPABILITY_GRANT_PERMISSIONS) && (
                          <PermissionEditorEditPermissions
                            isGlobal={true}
                            fieldId={customField.fieldId}
                            fieldName={customField ? customField.fieldName : '...'}
                            contextId={null}
                            contextName={'global'}
                            isDisabled={false /*customField.fieldIsLocked === true || customField.fieldIsCompatible !== true*/}
                          />
                        )}
                        <div style={{ marginLeft: '10px' }} data-e2e-edit-options-in-context={`${customField.fieldId}`}>
                          <Tooltip content={t('user.list.editButtonLabel')}>
                            <Button
                              iconBefore={
                                <EditFilledIcon
                                  size="medium"
                                  label="edit"
                                  primaryColor={
                                    false /* customField.fieldIsLocked === true || customField.fieldIsCompatible !== true */
                                      ? token('color.text.disabled', '#ccc')
                                      : token('color.text.accent.blue.bolder', '#253858')
                                  }
                                />
                              }
                              onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e.preventDefault();
                                navigate(encodeUriSafe('/fe/fields/:fieldId/contexts', [{ name: 'fieldId', value: customField.fieldId, type: 'path' }], false));
                              }}
                              appearance="subtle"
                              isDisabled={false /* customField.fieldIsLocked === true || customField.fieldIsCompatible !== true*/}
                            />
                          </Tooltip>
                        </div>
                      </PullRight>
                    )}
                  </div>
                ) : (
                  <>...</>
                )}
              </InnerTd>
            ),
          },
        ],
      });
    }
  }

  return (
    <PageWrapper>
      <CepPageHeader
        useNavigate={useNavigate}
        pageTitle={hasError ? t('global.pageLoadFailErrorHeadline') : t('user.list.pageTitle')}
        isLoading={customFieldsLoadingState === LoadingState.LOADING}
        breadcrumbItems={breadcrumbItems}
        helpUri={'/user-guide/field-editor/'}
        helpContent={
          <>
            <p>{t('user.list.notice.paragraph1')}</p>
            <ul>
              <li>{t('user.list.notice.paragraph2')}</li>
              <li>{tWithIcon('user.list.notice.paragraph3')}</li>
            </ul>
            {userCapabilities.includes(USER_CAPABILITY_GRANT_PERMISSIONS) && (
              <>
                <p>{t('user.list.notice.admin.paragraph1')}</p>
                <ul>
                  <li>{tWithIcon('user.list.notice.admin.paragraph2')}</li>
                  <li>{tWithIcon('user.list.notice.admin.paragraph3')}</li>
                </ul>
              </>
            )}
          </>
        }
      ></CepPageHeader>
      {hasError ? (
        <PageLoadError errorMessage={error} invokeKey={GET_ALL_CUSTOM_FIELDS} errorId={errorId ? errorId : 'ERRLDNG'} />
      ) : (
        <BetterDynamicTable
          rows={rows}
          head={head}
          hasFilter={true}
          hasPages={true}
          onSortChanged={() => {}}
          initialSort={{
            key: 'id',
            sortOrder: 'ASC',
          }}
          initialPage={currentPaginationState.currentPage}
          processingTime={currentPaginationState.processingTimeInMs}
          numPages={currentPaginationState.totalPages}
          onPageChanged={(newPage: number) => {
            setCurrentPaginationState((prev) => Object.assign({}, prev, { currentPage: newPage }));
          }}
          onFilterChanged={(f: string) => {
            setCurrentPaginationState((prev) => Object.assign({}, prev, { currentPage: 1 }));
            setCurrentFilter(f);
          }}
          initialFilter={currentFilter}
          isLoading={customFieldsLoadingState === LoadingState.LOADING}
          noResultsTitle={currentFilter !== '' ? t('user.list.results.noResultsOnFilter') : t('user.list.results.noResultsWithoutFilter')}
          noResultsDescription={currentFilter !== '' ? t('user.list.results.noResultsOnFilterDescription') : t('user.list.results.noResultsWithoutFilterDescription')}
        />
      )}
    </PageWrapper>
  );
};
